import {
  type Locale,
  format,
  formatDuration,
  intervalToDuration,
} from 'date-fns';

import { formatDatePast } from '@spotify/confidence-core-react';
import { RecordingRefFragment } from '@spotify/confidence-graphql';

import { DISPLAY_NAMES } from './constants';
import { METRIC_DISPLAY, getMetric } from './metrics';
import { parseDurationSeconds } from './utils';

export type RecordingRow = {
  name: string;
  displayName: string;
  time: string;
  duration: string;
  os: string;
  browser: string;
  pagesVisited: string[];
  pageCount: number;
  eventCount: number;
};

export const formatLabel = (value?: string, type?: string): string => {
  if (!value) return `Unknown ${type ?? ''}`;

  return DISPLAY_NAMES[value.toLowerCase()] ?? value;
};

export const formatRecordingTime = (dateString: string): string => {
  const date = new Date(dateString);
  const time = format(date, 'HH:mm');

  return `${formatDatePast(date)}, ${time}`;
};

const SHORT_DURATION_UNITS: Record<string, string> = {
  xHours: 'h',
  xMinutes: 'm',
  xSeconds: 's',
};

const shortDurationLocale: Locale = {
  formatDistance: (token, count) =>
    `${count}${SHORT_DURATION_UNITS[token] ?? ''}`,
} as Locale;

export const formatShortDuration = (seconds: number): string => {
  if (seconds === 0) return '0s';

  return formatDuration(intervalToDuration({ start: 0, end: seconds * 1000 }), {
    format: ['hours', 'minutes', 'seconds'],
    locale: shortDurationLocale,
  });
};

export const formatRecordingDuration = (
  duration: string | undefined,
): string => {
  if (!duration) return '-';

  const seconds = parseDurationSeconds(duration);

  if (seconds === undefined || seconds < 0) return '-';
  if (seconds < 1 && seconds > 0) return '< 1s';

  return formatShortDuration(seconds);
};

export const toRow = (recording: RecordingRefFragment): RecordingRow => {
  const eventCount = Object.entries(METRIC_DISPLAY)
    .filter(([, m]) => m.userInteraction)
    .reduce((sum, [key]) => sum + (getMetric(recording.metrics, key) ?? 0), 0);

  return {
    name: recording.name,
    displayName: recording.displayName ?? 'Unknown',
    time: formatRecordingTime(recording.createTime),
    duration: formatRecordingDuration(recording.duration ?? undefined),
    os: recording.environmentInfo?.os ?? '',
    browser: recording.environmentInfo?.browser ?? '',
    pagesVisited: (recording.routing?.routes ?? []).map(p => p.pageUri),
    pageCount: recording.routing?.routes?.length ?? 0,
    eventCount,
  };
};
